import { MockViolation } from '../types/mockdetector.types';
import { DatabaseViolation } from '../types/database.types';

/**
 * ✅ PHASE 4: Context-Aware Auto-Fix Suggestions
 * Provides specific, copy-paste-ready fixes based on violation type and context
 */

export type ViolationType = 
  | 'useState_array'
  | 'test_email'
  | 'lorem_ipsum'
  | 'array_fill'
  | 'inline_map'
  | 'mock_phone'
  | 'sequential_users'
  | 'hardcoded_array'
  | 'static_data'
  | 'test_username'
  | 'mock_address'
  | 'mock_company'
  | 'mock_credit_card'
  | 'mock_zip'
  | 'supabase_query'
  | 'api_call'
  | 'generic';

/**
 * Detect the specific type of violation
 */
export function detectViolationType(violation: MockViolation | DatabaseViolation): ViolationType {
  const code = 'code' in violation ? violation.code?.toLowerCase() || '' : '';
  const description = violation.description.toLowerCase();
  const pattern = 'pattern' in violation ? violation.pattern.toLowerCase() : '';

  // React patterns
  if (code.includes('usestate([') || description.includes('usestate')) {
    return 'useState_array';
  }
  if (code.includes('array.fill(') || code.includes('array(')) {
    return 'array_fill';
  }
  if (code.match(/\[\s*\{.*\}\s*\]\.map\(/)) {
    return 'inline_map';
  }

  // Email patterns
  if (code.includes('test@') || code.includes('@example.') || pattern.includes('email')) {
    return 'test_email';
  }

  // Text patterns
  if (code.includes('lorem ipsum') || code.includes('dolor sit')) {
    return 'lorem_ipsum';
  }

  // Phone patterns
  if (code.includes('555-') || pattern.includes('phone')) {
    return 'mock_phone';
  }

  // Sequential patterns
  if (pattern.includes('sequential') || description.includes('sequential')) {
    return 'sequential_users';
  }

  // Username patterns
  if (pattern.includes('username') || description.includes('test username')) {
    return 'test_username';
  }

  // Address patterns
  if (pattern.includes('address') || description.includes('mock address')) {
    return 'mock_address';
  }

  // Company patterns
  if (pattern.includes('company') || description.includes('company name')) {
    return 'mock_company';
  }

  // Credit card patterns
  if (pattern.includes('credit_card') || description.includes('credit card')) {
    return 'mock_credit_card';
  }

  // ZIP code patterns
  if (pattern.includes('zip') || description.includes('zip code')) {
    return 'mock_zip';
  }

  // Hardcoded arrays
  if (code.includes('[{') && code.includes('}]')) {
    return 'hardcoded_array';
  }

  // Static data
  if (description.includes('static') || description.includes('hardcoded')) {
    return 'static_data';
  }

  return 'generic';
}

/**
 * Generate context-specific fix suggestion
 */
export function generateContextualFix(violation: MockViolation | DatabaseViolation): string {
  const type = detectViolationType(violation);
  const tableName = 'table' in violation ? violation.table : 'your_table';

  switch (type) {
    case 'useState_array':
      return `// ✅ Replace with database fetch:
const [data, setData] = useState([]);

useEffect(() => {
  const fetchData = async () => {
    const { data, error } = await supabase
      .from('${tableName}')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (error) {
      console.error('Error fetching data:', error);
      return;
    }
    
    if (data) setData(data);
  };
  
  fetchData();
}, []);`;

    case 'test_email':
      return `// ✅ Use real user email from authentication:
const { data: { user } } = await supabase.auth.getUser();
const email = user?.email || 'support@yourdomain.com';

// OR fetch from database:
const { data } = await supabase
  .from('profiles')
  .select('email')
  .eq('id', userId)
  .single();`;

    case 'lorem_ipsum':
      return `// ✅ Replace with real content from database:
const { data } = await supabase
  .from('${tableName}')
  .select('description, content, bio')
  .eq('id', itemId)
  .single();

// OR use user-generated content:
const content = data?.description || 'No description available';`;

    case 'array_fill':
      return `// ❌ Array.fill() creates identical mock data!
// ✅ Replace with real database query:

const { data, error } = await supabase
  .from('${tableName}')
  .select('*')
  .limit(10);

if (error) throw error;
return data || [];`;

    case 'inline_map':
      return `// ✅ Move hardcoded data to state and fetch from database:

// 1. Create state:
const [items, setItems] = useState([]);

// 2. Fetch data:
useEffect(() => {
  const loadItems = async () => {
    const { data } = await supabase
      .from('${tableName}')
      .select('*');
    setItems(data || []);
  };
  loadItems();
}, []);

// 3. Use in JSX:
{items.map((item) => (
  <YourComponent key={item.id} {...item} />
))}`;

    case 'mock_phone':
      return `// ✅ Replace with real contact information:

// Option 1: From settings/config table
const { data } = await supabase
  .from('settings')
  .select('support_phone, contact_phone')
  .single();

// Option 2: From user profile
const { data: profile } = await supabase
  .from('profiles')
  .select('phone_number')
  .eq('id', userId)
  .single();

const phone = data?.support_phone || '+1 (555) 123-4567'; // Your real number`;

    case 'sequential_users':
      return `// ❌ Sequential user generation detected!
// ✅ Fetch real users from your database:

const { data: users, error } = await supabase
  .from('profiles')
  .select('id, name, email, avatar_url, created_at')
  .order('created_at', { ascending: false })
  .limit(20);

if (error) {
  console.error('Error fetching users:', error);
  return [];
}

return users || [];`;

    case 'test_username':
      return `// ✅ Use real usernames from authentication:

const { data } = await supabase
  .from('profiles')
  .select('username, display_name, full_name')
  .eq('id', userId)
  .single();

const username = data?.username || data?.display_name || 'Anonymous';`;

    case 'mock_address':
      return `// ✅ Store and fetch real addresses:

const { data } = await supabase
  .from('addresses')
  .select('street, city, state, zip_code, country')
  .eq('user_id', userId)
  .single();

const fullAddress = \`\${data.street}, \${data.city}, \${data.state} \${data.zip_code}\`;`;

    case 'mock_company':
      return `// ✅ Use real company information:

const { data } = await supabase
  .from('companies')
  .select('name, industry, size, founded_year')
  .eq('id', companyId)
  .single();

const companyName = data?.name || 'Your Company';`;

    case 'mock_credit_card':
      return `// ⚠️ NEVER store real credit cards in your database!
// ✅ Use Stripe or payment provider:

import { loadStripe } from '@stripe/stripe-js';

const stripe = await loadStripe('your_publishable_key');
const { error } = await stripe.confirmPayment({
  elements,
  confirmParams: {
    return_url: 'https://yourdomain.com/payment-success',
  },
});`;

    case 'mock_zip':
      return `// ✅ Fetch real ZIP code from user location or address:

const { data } = await supabase
  .from('addresses')
  .select('zip_code, postal_code')
  .eq('user_id', userId)
  .eq('is_primary', true)
  .single();

const zipCode = data?.zip_code || data?.postal_code;`;

    case 'hardcoded_array':
    case 'static_data':
      return `// ✅ Replace hardcoded data with database fetch:

const { data, error } = await supabase
  .from('${tableName}')
  .select('*')
  .order('created_at', { ascending: false });

if (error) {
  console.error('Database error:', error);
  return [];
}

return data || [];`;

    case 'generic':
    default:
      return `// ✅ Recommended fix:
// 1. Create a Supabase table to store this data
// 2. Replace hardcoded values with database queries
// 3. Use useState and useEffect to load data dynamically

const [data, setData] = useState([]);

useEffect(() => {
  const fetchData = async () => {
    const { data, error } = await supabase
      .from('${tableName}')
      .select('*');
    if (data) setData(data);
  };
  fetchData();
}, []);`;
  }
}

/**
 * Get quick action suggestions based on violation type
 */
export function getQuickActions(violation: MockViolation | DatabaseViolation): Array<{
  label: string;
  action: 'create_table' | 'add_auth' | 'use_api' | 'fetch_data' | 'migrate_data';
  description: string;
}> {
  const type = detectViolationType(violation);
  const tableName = 'table' in violation ? violation.table : 'items';

  const actions: Array<{
    label: string;
    action: 'create_table' | 'add_auth' | 'use_api' | 'fetch_data' | 'migrate_data';
    description: string;
  }> = [];

  switch (type) {
    case 'test_email':
    case 'test_username':
      actions.push({
        label: 'Add Authentication',
        action: 'add_auth',
        description: 'Set up Supabase Auth to manage real users'
      });
      break;

    case 'useState_array':
    case 'hardcoded_array':
    case 'array_fill':
      actions.push({
        label: `Create ${tableName} Table`,
        action: 'create_table',
        description: `Create a Supabase table to store ${tableName} data`
      });
      actions.push({
        label: 'Fetch from Database',
        action: 'fetch_data',
        description: 'Replace hardcoded data with database queries'
      });
      break;

    case 'lorem_ipsum':
      actions.push({
        label: 'Create Content Table',
        action: 'create_table',
        description: 'Store real content in a Supabase table'
      });
      break;

    case 'mock_phone':
    case 'mock_address':
      actions.push({
        label: 'Create Settings Table',
        action: 'create_table',
        description: 'Store contact information in database'
      });
      break;

    case 'sequential_users':
      actions.push({
        label: 'Migrate to Real Users',
        action: 'migrate_data',
        description: 'Replace mock users with real user profiles'
      });
      break;

    default:
      actions.push({
        label: 'Fetch from Database',
        action: 'fetch_data',
        description: 'Replace with real data from Supabase'
      });
  }

  return actions;
}

/**
 * Enhance violation with contextual fix
 */
export function enhanceViolationWithFix(violation: MockViolation | DatabaseViolation): MockViolation | DatabaseViolation {
  const contextualFix = generateContextualFix(violation);
  const quickActions = getQuickActions(violation);
  
  return {
    ...violation,
    fix: contextualFix,
    // Store quick actions in reasoning field for now (can add separate field later)
    reasoning: `Quick fixes available: ${quickActions.map(a => a.label).join(', ')}`
  };
}
